import { useEffect, useRef } from 'react';
import { EntityName } from '../../../auth/Security';
import type { DeepLinkUIStateOnBeforeInitializationHandler } from '../../types';
import { useDeepLinkContext } from './useDeepLinkContext';
import { useEntityDeepLinkUIStatePath } from './useEntityDeepLinkUIStatePath';
import {
    extractDeepLinkUIStateInitializationEvent,
    resolveAncestorEntityIdDeepLinkPaths,
    resolveEntityDeepLinkUIStateFromEntityData
} from './useInitializeDeepLinkUIState.utils';

type UseInitializeDeepLinkUIStateInput<T extends Record<string, unknown>> = {
    /**
     * Main entity type for the initialized deep-link state.
     *
     * This is always required and is used to:
     * 1. Build the main entity deep-link state node from returned entity data.
     * 2. Construct `entityDeepLinkUIStatePath` when it is not explicitly provided.
     */
    entity: EntityName;

    /**
     * Optional deep-link state path override for the main entity node.
     *
     * If not provided, the path is derived from `entity` and parent entity security context.
     */
    entityDeepLinkUIStatePath?: string;

    /**
     * Entity data used to build the main entity deep-link UI runtime state slice.
     *
     * If undefined, initialization is deferred and retried on subsequent renders.
     */
    entityDataRecord: T | undefined;

    /**
     * Optional callback invoked only after entity data has been validated and converted into a valid deep-link
     * state object, and before the runtime deep-link state slice is written.
     */
    onBeforeInitialize?: DeepLinkUIStateOnBeforeInitializationHandler;

    /**
     * Controls if the initial state value is merged with the existing state value or not.
     * @default true
     */
    merge?: boolean;
};

export function useInitializeDeepLinkUIState<T extends Record<string, unknown>>(
    input: UseInitializeDeepLinkUIStateInput<T>
): void {
    const isInitializationDone = useRef(false);
    const { entity, entityDeepLinkUIStatePath, entityDataRecord, onBeforeInitialize, merge = true } = input;

    const deepLinkContext = useDeepLinkContext();

    const resolvedEntityDeepLinkUIStatePath = useEntityDeepLinkUIStatePath(entity, entityDeepLinkUIStatePath);

    useEffect(() => {
        const finalizeInitialization = () => {
            isInitializationDone.current = true;

            const deepLinkInitialStatePathsToConsumeOnFinalize = resolveAncestorEntityIdDeepLinkPaths({
                deepLinkUIStatePath: resolvedEntityDeepLinkUIStatePath,
                includeResolvedPath: true
            });

            deepLinkInitialStatePathsToConsumeOnFinalize.forEach((path) => {
                deepLinkContext.getDeepLinkInitialState({
                    path,
                    clearAfterRead: true
                });
            });
        };

        const setTerminalFallbackState = () => {
            deepLinkContext.setDeepLinkRuntimeState(true, {
                path: resolvedEntityDeepLinkUIStatePath
            });

            finalizeInitialization();
        };

        const initialize = async () => {
            if (isInitializationDone.current) {
                return;
            }

            const maybeInitialState = deepLinkContext.getDeepLinkInitialState<Record<string, unknown> | undefined>({
                path: resolvedEntityDeepLinkUIStatePath
            });

            try {
                if (!entityDataRecord) {
                    return;
                }

                const initialValue = resolveEntityDeepLinkUIStateFromEntityData(entity, entityDataRecord);

                if (!initialValue) {
                    setTerminalFallbackState();
                    return;
                }

                const initializationEvent = extractDeepLinkUIStateInitializationEvent(
                    maybeInitialState as Record<string, unknown> | undefined
                );

                await onBeforeInitialize?.(initializationEvent);

                deepLinkContext.setDeepLinkRuntimeState(initialValue, {
                    path: resolvedEntityDeepLinkUIStatePath,
                    merge
                });

                finalizeInitialization();
            } catch (error) {
                console.error(
                    `Failed to initialize entity deep link UI runtime state for path ${resolvedEntityDeepLinkUIStatePath}`,
                    error
                );

                setTerminalFallbackState();
            }
        };

        initialize();
    }, [deepLinkContext, entity, entityDataRecord, merge, onBeforeInitialize, resolvedEntityDeepLinkUIStatePath]);
}
